import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SkeletonDirective } from '@meplis/directives';
import { ChatRoomView } from '@cnp/common';
import { ChatRoomStatus } from '@cnp/common';
import { ChatMessageType } from '@cnp/common';
import { ChatTimestampPipe } from '@cnp/pipes';
import { AvatarStackComponent } from '@cnp/components';

/**
 * Presentational conversation list. Shared by the header dropdown and the
 * /chat sidebar so both stay identical as the row design evolves -- `compact`
 * only trims padding for the dropdown.
 */
@Component({
  selector: 'app-chat-room-list',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    FaIconComponent,
    SkeletonDirective,
    ChatTimestampPipe,
    AvatarStackComponent,
  ],
  templateUrl: './chat-room-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './chat-room-list.component.scss',
})
export class ChatRoomListComponent {
  private _translate = inject(TranslateService);

  @Input() public rooms: ChatRoomView[] = [];
  @Input() public activeRoomId: string | null = null;
  @Input() public isLoading = false;
  @Input() public compact = false;

  @Output() public roomSelected = new EventEmitter<ChatRoomView>();

  public readonly roomStatus = ChatRoomStatus;

  public trackByRoom(_index: number, room: ChatRoomView): string {
    return room.id;
  }

  /** Everyone but the logged-in user -- your own face says nothing here. */
  public avatarIds(room: ChatRoomView): number[] {
    return (room.others || []).map((profile) => profile.id);
  }

  public isGroup(room: ChatRoomView): boolean {
    return room.otherCount > 1;
  }

  /**
   * System entries read as plain text in the list ("Ricardo added João"),
   * while a normal message is prefixed with who sent it in group rooms.
   */
  public previewOf(room: ChatRoomView): string {
    // While there's an unread backlog, anchor the preview on the oldest
    // unread message -- that's the one the user hasn't seen and would read
    // first, not whatever arrived most recently. Only available once a live
    // unread-rooms event has reported on this room this session; until then
    // (or once caught up) this falls through to the last message either way.
    // An encrypted unread message arrives without text on the unread queue;
    // the opened newest message is the better preview then (D5).
    const firstUnread = room.firstUnreadMessage;
    const message =
      room.unreadCount > 0 && firstUnread && !(firstUnread.e2ee && !firstUnread.content)
        ? firstUnread
        : room.lastMessage;

    const content = message?.content ?? room.lastMessageContent;
    if (!content) {
      return message?.e2ee || room.e2eeEnabled
        ? this._translate.instant('CHAT.E2EE.ENCRYPTED_MESSAGE')
        : '';
    }

    if (message?.type === ChatMessageType.SYSTEM) {
      return content;
    }

    const sender =
      message?.senderProfile?.firstname ?? room.lastMessageSender?.firstname;

    // Only group rooms need the "who said it" prefix; in a 1:1 the row title
    // already names the other person.
    return this.isGroup(room) && sender ? `${sender}: ${content}` : content;
  }

  public onSelect(room: ChatRoomView): void {
    this.roomSelected.emit(room);
  }
}
